import React, { useState, useEffect, useMemo, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { TextField, Button, Box, Typography, Container, MenuItem, Select, InputLabel, FormControl, Autocomplete, CircularProgress } from '@mui/material';
import { debounce } from 'lodash';
import { formatUtcForDateTimeLocal } from '../utils/dateTime';
import { formCardShadow } from '../styles/uiTokens';

function ConsumoAguaForm({ onConsumoAguaAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [leitura, setLeitura] = useState('');
  const [data_hora_leitura, setDataHoraLeitura] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  const [empresas, setEmpresas] = useState([]);
  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [loadingPessoas, setLoadingPessoas] = useState(false);

  const debouncedFetchPessoas = useMemo(
    () => debounce((searchTerm) => {
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${searchTerm}`)
        .then((response) => setPessoaOptions(response.data.data || response.data))
        .catch((error) => console.error('Erro ao buscar pessoas:', error))
        .finally(() => setLoadingPessoas(false));
    }, 500),
    []
  );

  useEffect(() => () => {
    debouncedFetchPessoas.cancel();
  }, [debouncedFetchPessoas]);

  useEffect(() => {
    if (user && user.tipo_usuario === 'admin') {
      api.get('/empresas')
        .then(response => {
          const list = response.data?.data || response.data;
          setEmpresas(Array.isArray(list) ? list : []);
        })
        .catch(error => {
          console.error('Erro ao buscar empresas:', error);
        });
    }

    const fetchPessoaById = async (id) => {
      if (!id) {
        setSelectedPessoa(null);
        return;
      }
      try {
        const response = await api.get(`/pessoas?id=${id}`);
        const item = (response.data.data || response.data)[0];
        if (item) {
          setSelectedPessoa(item);
          setPessoaOptions([item]);
        } else {
          setSelectedPessoa(null);
        }
      } catch (error) {
        console.error('Erro ao buscar pessoa por ID:', error);
        setSelectedPessoa(null);
      }
    };

    if (initialData) {
      setId(initialData.id);
      setEmpresaId(initialData.empresa_id);
      setPessoaId(initialData.pessoa_id || '');
      setLeitura(initialData.leitura != null ? initialData.leitura : '');
      setDataHoraLeitura(formatUtcForDateTimeLocal(initialData.data_hora_leitura, user));
      fetchPessoaById(initialData.pessoa_id);
    } else {
      setId(null);
      setEmpresaId('');
      setLeitura('');
      setDataHoraLeitura('');
      if (user?.pessoa_id) {
        setPessoaId(user.pessoa_id);
        api.get(`/pessoas?id=${user.pessoa_id}`)
          .then(response => {
            const items = response.data.data || response.data;
            const pessoa = Array.isArray(items) ? items[0] : items;
            if (pessoa) {
              setSelectedPessoa(pessoa);
              setPessoaOptions([pessoa]);
            }
          })
          .catch(err => console.error('Erro ao buscar pessoa do usuario:', err));
      } else {
        setPessoaId('');
        setSelectedPessoa(null);
        setPessoaOptions([]);
      }
    }
  }, [initialData, user]);

  const clearForm = () => {
    setId(null);
    setEmpresaId('');
    setLeitura('');
    setDataHoraLeitura('');
    if (user?.pessoa_id) {
      setPessoaId(user.pessoa_id);
      api.get(`/pessoas?id=${user.pessoa_id}`)
        .then(response => {
          const items = response.data.data || response.data;
          const pessoa = Array.isArray(items) ? items[0] : items;
          if (pessoa) {
            setSelectedPessoa(pessoa);
            setPessoaOptions([pessoa]);
          }
        })
        .catch(err => console.error('Erro ao buscar pessoa do usuario:', err));
    } else {
      setPessoaId('');
      setSelectedPessoa(null);
      setPessoaOptions([]);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);

    const consumoAguaData = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      pessoa_id,
      leitura,
      data_hora_leitura,
    };

    if (id) {
      api.put(`/consumo-agua/${id}`, consumoAguaData)
        .then(response => {
          console.log('Consumo de agua atualizado:', response.data);
          clearForm();
          onConsumoAguaAdded();
          onCancelEdit();
        })
        .catch(error => {
          console.error('Erro ao atualizar consumo de agua:', error);
        })
        .finally(() => {
          submittingRef.current = false;
          setSubmitting(false);
        });
    } else {
      api.post('/consumo-agua', consumoAguaData)
        .then(response => {
          console.log('Consumo de agua adicionado:', response.data);
          clearForm();
          onConsumoAguaAdded();
        })
        .catch(error => {
          console.error('Erro ao adicionar consumo de agua:', error);
        })
        .finally(() => {
          submittingRef.current = false;
          setSubmitting(false);
        });
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 3,
          p: { xs: 2, sm: 3, md: 4 },
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          boxShadow: formCardShadow
        }}
      >
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Consumo de Água' : 'Adicionar Consumo de Água'}
        </Typography>

        {(user && user.tipo_usuario === 'admin') && (
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="empresa-select-label">Empresa</InputLabel>
            <Select
              labelId="empresa-select-label"
              value={empresa_id}
              label="Empresa"
              onChange={(e) => setEmpresaId(e.target.value)}
            >
              {(Array.isArray(empresas) ? empresas : []).map((emp) => (
                <MenuItem key={emp.id} value={emp.id}>
                  {emp.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Autocomplete
          options={pessoaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPessoa}
          onChange={(event, newValue) => {
            setSelectedPessoa(newValue);
            setPessoaId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) debouncedFetchPessoas(newInputValue);
              else setPessoaOptions([]);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Funcionário que Mediu"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          label="Leitura do Hidrômetro (m³)"
          variant="outlined"
          fullWidth
          type="number"
          value={leitura}
          onChange={(e) => setLeitura(e.target.value)}
          margin="normal"
          required
          inputProps={{ step: '0.001', min: '0' }}
        />

        <TextField
          label="Data e Hora da Leitura"
          variant="outlined"
          fullWidth
          type="datetime-local"
          value={data_hora_leitura}
          onChange={(e) => setDataHoraLeitura(e.target.value)}
          margin="normal"
          required
          InputLabelProps={{ shrink: true }}
          helperText={`Fuso da empresa: ${user?.empresa_timezone || 'America/Sao_Paulo'}`}
        />

        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2, mr: 2 }}
          disabled={submitting}
        >
          {submitting ? <CircularProgress size={24} color="inherit" /> : (id ? 'Atualizar Consumo' : 'Adicionar Consumo')}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onCancelEdit}
          disabled={submitting}
        >
          Cancelar
        </Button>
      </Box>
    </Container>
  );
}

export default ConsumoAguaForm;
